import { supabase } from './supabaseClient';

export const orderService = {
  /**
   * Create a new order in Supabase
   * This is called from the frontend after payment verification
   */
  async createOrder(orderData) {
    try {
      // Get the current authenticated user from Supabase
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error getting current user:', userError);
        // Allow guest orders - don't throw error
      }

      // Use the authenticated user's ID, or fall back to the provided userId
      const userId = user?.id || orderData.userId;
      
      // Generate order number
      const orderNumber = orderData.orderNumber || `CV-${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
      const now = new Date().toISOString();

      const { data, error } = await supabase
        .from('orders')
        .insert([
          {
            order_number: orderNumber,
            status: orderData.status || 'pending',
            total: parseFloat(orderData.total.toFixed(2)), // Ensure numeric(10,2) format
            items: orderData.items || [], // JSONB - Supabase handles conversion
            shipping_info: orderData.shippingInfo || orderData.shippingAddress || {}, // JSONB - Supabase handles conversion
            payment_intent_id: orderData.paymentIntentId || null,
            user_id: userId || null,
            tracking_number: null,
            payment_method: null, // Will be set by webhook
            payment_method_last4: null, // Will be set by webhook
            payment_method_brand: null, // Will be set by webhook
            apple_pay_transaction_id: null, // Will be set by webhook if applicable
            created_at: now,
            updated_at: now,
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('Order creation error:', error);
        throw new Error(`Failed to create order: ${error.message}`);
      }

      console.log('✅ Order created in Supabase:', data.id);

      return {
        id: data.id,
        orderNumber: data.order_number,
        status: data.status,
        total: data.total,
        items: data.items || [],
        shippingInfo: data.shipping_info || {},
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        trackingNumber: data.tracking_number,
        paymentIntentId: data.payment_intent_id,
        userId: data.user_id,
      };
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  /**
   * Get orders for the current authenticated user
   */
  async getUserOrders(userId) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch orders: ${error.message}`);
      }

      return (data || []).map(order => ({
        id: order.id,
        orderNumber: order.order_number,
        status: order.status,
        total: order.total,
        items: order.items || [],
        shippingInfo: order.shipping_info || {},
        createdAt: order.created_at,
        updatedAt: order.updated_at,
        trackingNumber: order.tracking_number,
        paymentIntentId: order.payment_intent_id,
        userId: order.user_id,
        checkoutSessionId: order.checkout_session_id,
        shipmentPhoto: order.shipment_photo, // If you add this field to orders table
      }));
    } catch (error) {
      console.error('Error fetching user orders:', error);
      throw error;
    }
  },

  /**
   * Get a specific order by ID
   */
  async getOrderById(orderId) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Order not found
        }
        throw new Error(`Failed to fetch order: ${error.message}`);
      }

      return {
        id: data.id,
        orderNumber: data.order_number,
        status: data.status,
        total: data.total,
        items: data.items || [],
        shippingInfo: data.shipping_info || {},
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        trackingNumber: data.tracking_number,
        paymentIntentId: data.payment_intent_id,
        userId: data.user_id,
        checkoutSessionId: data.checkout_session_id,
        shipmentPhoto: data.shipment_photo,
      };
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  },
};

